import pencilIcon from "@assets/images/pencil-alt.svg";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUp from "@material-ui/icons/KeyboardArrowUp";
import { useUser } from "@saleor/auth";
import {
  useDropdownListMaterialGroup1Query,
  useExportSoldTosLazyQuery,
  useFilterRequireAttentionMaterialLazyQuery,
  useSuggestionSearchSalesOrganizationReportOrderPendingQuery,
  useSuggestionSearchShipToReportOrderPendingQuery
} from "@saleor/graphql";
import { FIELD_TYPE } from "@saleor/PendingOrder/components/OrderFilterSectionAdmin/constant";
import { useFormik } from "formik";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { getProductGroup } from "./utils";

const usePendingOrderCustomer = ({ handleChangeFilter }) => {
  const intl = useIntl();
  const {
    data: salesOrg
  } = useSuggestionSearchSalesOrganizationReportOrderPendingQuery();
  const { data: productGroup } = useDropdownListMaterialGroup1Query({
    variables: { bu: "PP" }
  });

  const productGroupList = getProductGroup(
    productGroup,
    intl.formatMessage({ defaultMessage: "All" })
  );
  const transactionList = [
    {
      label: intl.formatMessage({
        defaultMessage: "Normal"
      }),
      value: "Normal"
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Delivery block"
      }),
      value: "Delivery block"
    }
  ];
  const { listCompany, company } = useUser();
  const soldToCode = listCompany?.find(e => e.soldToCode === company)
    ?.soldToCode;
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      soldTo: `${soldToCode} - ${
        listCompany?.find(e => e.soldToCode === company)?.soldToName
      }`,
      materialNoMaterialDescription: [],
      createDate: {
        gte: null,
        lte: null
      },
      requestDeliveryDate: {
        gte: null,
        lte: null
      },
      saleOrg:
        salesOrg?.suggestionSearchSalesOrganizationReportOrderPending.map(
          e => e.code
        ) || [],
      productGroup: "All",
      shipTo: {
        code: "",
        name: ""
      },
      poNo: "",
      soNo: "",
      transactions: transactionList.map(item => item.value)
    },
    onSubmit: data => {
      const submitData = {
        ...data,
        productGroups: data.productGroup
      };
      delete submitData.productGroup;

      if (!submitData.createDate.gte || !submitData.createDate.lte) {
        delete submitData.createDate;
      }

      if (
        !submitData.requestDeliveryDate.gte ||
        !submitData.requestDeliveryDate.lte
      ) {
        delete submitData.requestDeliveryDate;
      }

      delete submitData.shipTo;
      submitData.shipTo = data.shipTo.code as undefined;
      for (const [key, value] of Object.entries(submitData)) {
        if (!value) {
          delete submitData[key];
        }
      }

      if (!formik.values.saleOrg?.length) {
        const errorFields = [
          {
            fieldName: "saleOrg",
            value: formik.values.saleOrg?.length
          }
        ];
        errorFields.forEach(e => {
          if (!e.value) {
            formik.setFieldError(e.fieldName, "โปรดระบุข้อมูล");
          }
        });
        return;
      }
      handleChangeFilter(submitData);
    }
  });

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const [
    getRequireAttentionSoldTo,
    {
      data: fetchRequireAttentionSoldTo,
      loading: loadingRequireAttentionSoldTo
    }
  ] = useExportSoldTosLazyQuery();

  const {
    data: suggestionSearchShipToReportOrder
  } = useSuggestionSearchShipToReportOrderPendingQuery({
    variables: {
      soldToCode
    }
  });

  const [
    getRequireAttentionMaterial,
    {
      data: fetchRequireAttentionMaterial,
      loading: loadingRequireAttentionMaterial
    }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const ArrowIconComponent = useMemo(
    () => (isOpen ? KeyboardArrowUp : KeyboardArrowDown),
    [isOpen]
  );

  const handleCleanForm = useCallback(() => {
    setIsClean(true);
    formik.resetForm();
    setTimeout(() => setIsClean(false), 500);
  }, []);

  const shipToData =
    suggestionSearchShipToReportOrder?.suggestionSearchShipToReportOrderPending?.map(
      data => ({
        name: `${data.code} - ${data.name}`,
        code: data.code
      })
    ) || [];

  const formData = useMemo(
    () => [
      {
        title: intl.formatMessage({
          defaultMessage: "Sold to"
        }),
        name: "soldTo",
        type: FIELD_TYPE.DISABLED_TEXT,
        md: 6,
        sm: 12
      },

      {
        title: intl.formatMessage({ defaultMessage: "Ship-to" }),
        name: "shipTo",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Ship-to"
        }),
        type: FIELD_TYPE.BUTTON_SHIP_TO,
        iconRight: pencilIcon,
        isShipTo: true,
        md: 6,
        sm: 12
      },
      {
        title: intl.formatMessage({ defaultMessage: "Order Create Date" }),
        name: "createDate",
        required: true,
        type: FIELD_TYPE.DATEPICKER,
        maxLength: 35,
        md: 6,
        sm: 12
      },
      {
        title: intl.formatMessage({ defaultMessage: "Request Date" }),
        name: "requestDeliveryDate",
        type: FIELD_TYPE.DATEPICKER,
        maxLength: 35,
        md: 6,
        sm: 12
      },
      {
        title: intl.formatMessage({ defaultMessage: "Sales Organization" }),
        name: "saleOrg",
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Sales Organization"
        }),
        type: FIELD_TYPE.MULTIPLE_SELECT,
        required: true,
        options:
          salesOrg?.suggestionSearchSalesOrganizationReportOrderPending.map(
            e => ({
              label: `${e.code} - ${e.name}`,
              value: e.code
            })
          ) || [],
        md: 3
      },
      {
        title: intl.formatMessage({ defaultMessage: "Product Group" }),
        name: "productGroup",
        required: true,
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Product Group"
        }),
        type: FIELD_TYPE.SINGLE_SELECT,
        options: productGroupList,
        md: 3
      },
      {
        title: intl.formatMessage({
          defaultMessage: "Material Code / Description"
        }),
        subTitle: "(เลือกค้นหาได้ไม่เกิน 1 รายการ)",
        name: "materialNoMaterialDescription",
        placeholder: intl.formatMessage({
          defaultMessage: "ค้นหา Material Code / Description"
        }),
        type: FIELD_TYPE.MULTIPLE_SUGGESTION_INPUT,
        message: "ยังไม่มีข้อมูลที่เลือกค้นหา",
        maxLength: 35,
        headerText: "Material Code / Description ที่เลือกค้นหา",
        keyFilterApi: "material",
        md: 6,
        isShowSelected: false
      },
      {
        title: intl.formatMessage({
          defaultMessage: "PO No."
        }),
        name: "poNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ PO No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35,
        md: 3
      },
      {
        title: intl.formatMessage({
          defaultMessage: "S/O No."
        }),
        name: "soNo",
        placeholder: intl.formatMessage({
          defaultMessage: "ระบุ S/O No."
        }),
        type: FIELD_TYPE.SINGLE_INPUT,
        maxLength: 35,
        md: 3
      },

      {
        type: FIELD_TYPE.EMPTY,
        md: 6
      },
      {
        title: intl.formatMessage({ defaultMessage: "Transaction" }),
        name: "transactions",
        type: FIELD_TYPE.MULTIPLE_SELECT,
        placeholder: intl.formatMessage({
          defaultMessage: "เลือก Transaction"
        }),
        md: 3,
        options: transactionList,
        maxLength: 10
      }
    ],
    [salesOrg, suggestionSearchShipToReportOrder, productGroupList]
  );

  const setDataFormik = data => {
    formik.setValues(data);
  };

  return useMemo(
    () => ({
      formik,
      setDataFormik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getRequireAttentionSoldTo,
      fetchRequireAttentionSoldTo,
      loadingRequireAttentionSoldTo,
      getRequireAttentionMaterial,
      fetchRequireAttentionMaterial,
      loadingRequireAttentionMaterial,
      shipToData
    }),
    [
      formik,
      isOpen,
      setIsOpen,
      isClean,
      setIsClean,
      ArrowIconComponent,
      handleCleanForm,
      formData,
      getRequireAttentionSoldTo,
      fetchRequireAttentionSoldTo,
      loadingRequireAttentionSoldTo,
      getRequireAttentionMaterial,
      fetchRequireAttentionMaterial,
      loadingRequireAttentionMaterial,
      shipToData
    ]
  );
};

export default usePendingOrderCustomer;
